"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.seoBacklinks = exports.seoBacklinksResultSchema = exports.seoBacklinksArgsSchema = exports.defaultExportColumns = exports.actionMetadata = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// SEO Review Tools Backlinks Action Types - Limited to available API endpoints
var backlinksActions = [
    'GetBacklinkOverview',
    'GetDetailedBacklinks',
    'GetReferringDomainsCount',
];
// Action metadata for UI guidance with use cases and workflow stages
exports.actionMetadata = {
    GetBacklinkOverview: {
        description: 'Domain authority and backlink profile metrics: total backlinks, DA score (0-100), spam score, indexed pages, broken backlinks, referring domains/pages/IPs, and nofollow links. Returns single row with 10 key metrics.',
        useCases: [
            'Domain authority assessment for SEO competitive analysis',
            'Backlink health check and spam risk evaluation',
            'Quick profile summary for client reporting and dashboards',
            'Baseline metrics before launching link building campaigns',
        ],
        workflowStages: ['discovery', 'audit', 'planning', 'reporting'],
        businessContext: 'Foundation metrics for backlink strategy - use this first to understand overall link profile strength and health',
    },
    GetDetailedBacklinks: {
        description: 'Individual backlink data with source URL, domain, page/domain authority, anchor text, link type, dofollow status, spam score, external links, and status flags (new/lost/broken). Returns up to 100 backlinks.',
        useCases: [
            'Link quality audit and toxic backlink identification',
            'Anchor text distribution analysis and over-optimization detection',
            'Competitor backlink source discovery for outreach',
            'Monitoring new and lost backlinks for ongoing campaigns',
        ],
        workflowStages: ['audit', 'optimization', 'analysis', 'monitoring'],
        businessContext: 'Deep-dive analysis of link quality and sources - essential for link cleanup, competitor research, and outreach targeting',
    },
    GetReferringDomainsCount: {
        description: 'Bulk referring domains count for up to 1000 URLs in single request. Returns URL and referring domain count for each. Efficient for large-scale competitive analysis.',
        useCases: [
            'Multi-competitor backlink strength comparison',
            'Portfolio-wide link authority assessment',
            'Competitive gap analysis across market segment',
            'Bulk domain authority scoring for link prospects',
        ],
        workflowStages: ['competitive-research', 'analysis', 'reporting', 'planning'],
        businessContext: 'Scalable competitive intelligence - compare backlink strength across dozens or hundreds of domains simultaneously',
    },
};
// Default export columns for each action (no user configuration needed)
// Column names match actual CSV headers output by the API
exports.defaultExportColumns = {
    GetBacklinkOverview: [
        'Backlinks Total',
        'Domain Authority',
        'Indexed Pages',
        'Spam Score',
        'Broken Backlinks',
        'Domains',
        'Unique Domains',
        'Referring Pages',
        'Referring IPs',
        'Nofollow Links',
    ],
    GetDetailedBacklinks: [
        'Source URL',
        'Domain',
        'Page Authority',
        'Domain Authority',
        'Anchor Text',
        'Link Type',
        'Dofollow',
        'First Seen',
        'Last Seen',
        'External Links',
        'Link Is Lost',
        'Link Is New',
        'Broken Status',
        'Spam Score',
    ],
    GetReferringDomainsCount: ['URL', 'Referring Domains'],
};
// Sort Types
var sortTypes = ['asc', 'desc'];
// Database Options
var databases = [
    'us',
    'uk',
    'ca',
    'ru',
    'de',
    'fr',
    'es',
    'it',
    'br',
    'au',
    'ar',
    'be',
    'ch',
    'dk',
    'fi',
    'hk',
    'ie',
    'il',
    'mx',
    'nl',
    'no',
    'pl',
    'se',
    'sg',
    'tr',
    'jp',
    'in',
];
exports.seoBacklinksArgsSchema = zod_1.z.object({
    action: zod_1.z
        .enum(backlinksActions)
        .describe('Backlink analysis action:\n' +
        '• GetBacklinkOverview: Domain authority & backlink metrics (10 key metrics: total backlinks, DA score, spam score, referring domains, etc.) - Use for domain health assessment\n' +
        '• GetDetailedBacklinks: Individual backlink details (source URLs, anchor text, link quality, status) - Use for link audits and competitor research\n' +
        '• GetReferringDomainsCount: Bulk referring domains count for up to 1000 URLs - Use for large-scale competitive analysis'),
    target: zod_1.z
        .string()
        .describe('The target domain or URL to analyze. For GetReferringDomainsCount: comma-separated list of URLs (e.g., "example.com,competitor.com,another.com"). For other actions: single URL. Supports flexible formats - protocol is optional and will be auto-normalized. Format depends on targetType:\n' +
        '• For root_domain: Root domain only (e.g., "example.com", "https://example.com")\n' +
        '• For domain: Any domain including subdomains (e.g., "blog.example.com", "www.example.com")\n' +
        '• For url: Domain with optional path (e.g., "example.com/page", "https://example.com/article")\n' +
        'Protocol (https://) will be auto-added if missing.'),
    targetType: zod_1.z
        .enum(['root_domain', 'domain', 'url'])
        .describe('Target type specification:\n' +
        '• root_domain: Analyze root domain only (supports GetBacklinkOverview, GetDetailedBacklinks, GetReferringDomainsCount). Example: "example.com"\n' +
        '• domain: Analyze domain including subdomains (supports GetBacklinkOverview, GetDetailedBacklinks, GetReferringDomainsCount). Example: "blog.example.com"\n' +
        '• url: Analyze specific URL (supports GetDetailedBacklinks, GetReferringDomainsCount - NOT GetBacklinkOverview). Example: "example.com/page"'),
    sortType: zod_1.z.enum(sortTypes).default('desc').describe('Sort order for results (default: desc)'),
    database: zod_1.z.enum(databases).default('us').describe('The database to query (default: us)'),
    limit: zod_1.z
        .number()
        .min(1)
        .max(100)
        .default(10)
        .describe('Number of results to fetch (default 10, max 100). Keep at default unless user requests more data.'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the data in.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if the search returns no results. Default to true.'),
    })
        .describe('Search options'),
});
// Column definitions for each action type - keys match actual CSV column headers
var columnDefinitions = zod_1.z.object({
    GetBacklinkOverview: zod_1.z
        .object({
        'Backlinks Total': zod_1.z.number().describe('Total backlinks pointing to domain'),
        'Domain Authority': zod_1.z.number().describe('Domain Authority (0-100, higher is better)'),
        'Indexed Pages': zod_1.z.number().describe('Pages indexed in search engines'),
        'Spam Score': zod_1.z.number().describe('Spam risk score (0-100, lower is better)'),
        'Broken Backlinks': zod_1.z.number().describe('Count of broken/dead backlinks'),
        Domains: zod_1.z.number().describe('Total referring domains'),
        'Unique Domains': zod_1.z.number().describe('Unique referring domains'),
        'Referring Pages': zod_1.z.number().describe('Total pages linking to this domain'),
        'Referring IPs': zod_1.z.number().describe('Unique IP addresses of referring domains'),
        'Nofollow Links': zod_1.z.number().describe('Nofollow backlinks (do not pass link equity)'),
    })
        .describe('Domain-level backlink metrics (single row with 10 metrics)'),
    GetDetailedBacklinks: zod_1.z
        .object({
        'Source URL': zod_1.z.string().describe('Source page URL (where backlink is from)'),
        Domain: zod_1.z.string().describe('Source domain'),
        'Page Authority': zod_1.z.number().describe('Page Authority 0-100 (higher = stronger page)'),
        'Domain Authority': zod_1.z.number().describe('Domain Authority 0-100 (higher = stronger domain)'),
        'Anchor Text': zod_1.z.string().describe('Clickable text of the backlink'),
        'Link Type': zod_1.z.string().describe('Link format (text, image, redirect, etc.)'),
        Dofollow: zod_1.z.string().describe('Yes (passes link equity) or No (nofollow)'),
        'First Seen': zod_1.z.string().describe('Date backlink first discovered'),
        'Last Seen': zod_1.z.string().describe('Date backlink last verified'),
        'External Links': zod_1.z
            .number()
            .describe('Outbound links on source page (link dilution indicator)'),
        'Link Is Lost': zod_1.z.string().describe('Yes if link removed/lost, No if still active'),
        'Link Is New': zod_1.z.string().describe('Yes if recently discovered, No if existing'),
        'Broken Status': zod_1.z.string().describe('Yes if link returns 404 or error, No if working'),
        'Spam Score': zod_1.z.number().describe('Spam risk of source domain (0-100, lower is better)'),
    })
        .describe('Individual backlink details (multiple rows, max 100)'),
    GetReferringDomainsCount: zod_1.z
        .object({
        URL: zod_1.z.string().describe('Analyzed URL or domain'),
        'Referring Domains': zod_1.z.number().describe('Count of unique domains linking to this URL'),
    })
        .describe('Bulk domain count (one row per input URL, up to 1000 URLs)'),
});
exports.seoBacklinksResultSchema = zod_1.z.object({
    csvData: zod_1.z
        .string()
        .describe('Semicolon-delimited CSV containing all backlink analysis data. GetBacklinkOverview: Backlinks Total, Domain Authority, Indexed Pages, Spam Score, Broken Backlinks, Domains, Unique Domains, Referring Pages, Referring IPs, Nofollow Links (1 row). GetDetailedBacklinks: Source URL, Domain, Page Authority, Domain Authority, Anchor Text, Link Type, Dofollow, First Seen, Last Seen, External Links, Link Is Lost, Link Is New, Broken Status, Spam Score (up to 100 rows). GetReferringDomainsCount: URL, Referring Domains (1 row per input URL).'),
    query: zod_1.z.object({
        action: zod_1.z
            .string()
            .describe('Action performed (GetBacklinkOverview, GetDetailedBacklinks, or GetReferringDomainsCount)'),
        target: zod_1.z
            .string()
            .describe('Target analyzed (single domain/URL or comma-separated list for bulk)'),
        targetType: zod_1.z.string().describe('Target type (root_domain, domain, or url)'),
        database: zod_1.z.string().describe('Geographic database used (us, uk, ca, etc.)'),
        limit: zod_1.z.number().describe('Result limit (1-100, only applies to GetDetailedBacklinks)'),
    }),
    columnSchema: zod_1.z
        .union([
        columnDefinitions.shape.GetBacklinkOverview,
        columnDefinitions.shape.GetDetailedBacklinks,
        columnDefinitions.shape.GetReferringDomainsCount,
        zod_1.z.record(zod_1.z.string()),
    ])
        .describe('Column definitions for the CSV data. GetBacklinkOverview: 10 domain metrics. GetDetailedBacklinks: 14 per-link fields. GetReferringDomainsCount: URL + count.'),
    metadata: zod_1.z.object({
        executedAt: zod_1.z.string().datetime(),
        recordCount: zod_1.z
            .number()
            .optional()
            .describe('Rows returned (1 for Overview, up to 100 for Detailed, count matches input for Bulk)'),
        actionType: zod_1.z
            .enum(['GetBacklinkOverview', 'GetDetailedBacklinks', 'GetReferringDomainsCount'])
            .describe('Action type identifier for parsing column schema'),
    }),
});
exports.seoBacklinks = (0, common_1.createEmitModeToolMetadata)({
    id: 'seoBacklinks',
    displayName: 'Backlink Intelligence',
    displayDescription: 'Comprehensive backlink analysis powered by SEO Review Tools. Provides domain authority metrics (DA 0-100, spam score, referring domains), individual backlink details (source URLs, anchor text, link quality), and bulk competitive analysis (up to 1000 URLs). Essential for link audits, competitor research, and SEO strategy.',
    category: 'seo',
    color: '#0066CC',
    backgroundColor: (0, common_1.createBgColor)('#0066CC'),
    icon: 'search',
    argsSchema: exports.seoBacklinksArgsSchema,
}, exports.seoBacklinksResultSchema);
